import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EquipmentTypeField } from '@/components/EquipmentTypeSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { allEquipmentTypesState, spacesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { EquipmentTestLibraryDao, createId } from '@rock/db-admin';
import type {
    EquipmentTestLibraryEntry,
    EquipmentTestLibraryEntryId,
    EquipmentTypeId,
    ImageOption,
    ModelNumber,
    SerialNumber,
    SpaceId,
    Timestamp,
} from '@rock/types';
import { orderBy } from 'lodash';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImageSlotField } from './ImageSlotField';
import { isPhotographableType } from './libraryTypes';

const log = logger('EntryFormDialog');
const equipmentTestLibraryDao = new EquipmentTestLibraryDao();

/**
 * Starting values for a NEW entry. Ranges from just a type (the "add one of these" affordance on an
 * empty type pill) to a whole entry lifted off a home's real equipment, photos included.
 */
export interface EntryPrefill {
    spaceId?: SpaceId;
    typeId?: EquipmentTypeId;
    name?: string;
    brand?: string;
    modelNumber?: ModelNumber;
    serialNumber?: SerialNumber;
    productImage?: ImageOption;
    nameplateImage?: ImageOption;
    notes?: string;
}

/**
 * Add or edit a library entry. One form for both: the fields are identical, and the only thing that
 * differs is whether an id already exists.
 *
 * `source` is derived rather than asked: an entry counts as own-photo only when nothing on it came
 * from a web search, which is what the badge in the list is actually telling you.
 */
export const EntryFormDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Editing an existing entry, or undefined to add a new one. */
    entry?: EquipmentTestLibraryEntry;
    /** Starting values when adding. Ignored when `entry` is set — an edit starts from the entry. */
    prefill?: EntryPrefill;
}> = ({ open, onOpenChange, entry, prefill }) => {
    const spaces = useAsyncAtomValue(spacesState, []);
    const equipmentTypes = useAsyncAtomValue(allEquipmentTypesState, []);

    const [spaceId, setSpaceId] = useState<SpaceId | undefined>();
    const [typeId, setTypeId] = useState<EquipmentTypeId | undefined>();
    const [name, setName] = useState('');
    const [brand, setBrand] = useState('');
    const [modelNumber, setModelNumber] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [notes, setNotes] = useState('');
    const [productImage, setProductImage] = useState<ImageOption | undefined>();
    const [nameplateImage, setNameplateImage] = useState<ImageOption | undefined>();
    // Whether either photo came from a web search this session — the entry's `source`.
    const [usedSearch, setUsedSearch] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // A stable id from the moment the dialog opens: the photo slots upload to a path under it, so it
    // has to exist before the entry is saved.
    const [entryId, setEntryId] = useState<EquipmentTestLibraryEntryId>(() => createId());

    // The form resets ONLY when the dialog opens, never while it's open. The seed is read through a
    // ref rather than listed as a dependency because neither input has a stable identity — a caller
    // may build `prefill` inline each render, and the types atom hands back a fresh `[]` until it
    // resolves. Re-running on those would mint a new `entryId` under a user mid-edit, stranding any
    // photo already uploaded under the old one (nothing reaps a folder no entry ever claimed) and
    // wiping what they'd typed.
    const seedRef = useRef({ entry, prefill });
    seedRef.current = { entry, prefill };
    useEffect(() => {
        if (!open) return;
        // An edit starts from the entry; an add starts from the prefill (possibly empty).
        const { entry: editing, prefill: starting } = seedRef.current;
        const seed = editing ?? starting;
        setEntryId(editing?.id ?? createId<EquipmentTestLibraryEntryId>());
        setSpaceId(seed?.spaceId);
        setTypeId(seed?.typeId);
        setName(seed?.name ?? '');
        setBrand(seed?.brand ?? '');
        setModelNumber(seed?.modelNumber ?? '');
        setSerialNumber(seed?.serialNumber ?? '');
        setNotes(seed?.notes ?? '');
        setProductImage(seed?.productImage);
        setNameplateImage(seed?.nameplateImage);
        setUsedSearch(false);
    }, [open]);

    // A prefill may name only a type (the empty-pill affordance) with no space. Fill the space in from
    // the type whenever it's known — including when the catalog finishes loading after the dialog
    // opened — but only ever fill a blank, so it never fights the reset above or the user's own pick.
    useEffect(() => {
        if (spaceId || !typeId) return;
        const type = equipmentTypes.find((candidate) => candidate.id === typeId);
        if (type) setSpaceId(type.spaceId);
    }, [spaceId, typeId, equipmentTypes]);

    const typesInSpace = useMemo(
        () =>
            orderBy(
                equipmentTypes.filter((type) => type.spaceId === spaceId && isPhotographableType(type)),
                ['order', 'title'],
            ),
        [equipmentTypes, spaceId],
    );
    const selectedType = useMemo(() => equipmentTypes.find((type) => type.id === typeId), [equipmentTypes, typeId]);

    // Default the display name to the type once one is picked — typing a name for every entry is
    // busywork, and "Carrier Central AC" is what you'd have written anyway. Once per type, not on
    // every empty render: otherwise clearing the field just puts the default straight back.
    const defaultedForTypeRef = useRef<EquipmentTypeId | undefined>(undefined);
    useEffect(() => {
        if (!selectedType || defaultedForTypeRef.current === selectedType.id) return;
        defaultedForTypeRef.current = selectedType.id;
        if (!name) setName(`${brand} ${selectedType.title}`.trim());
    }, [selectedType, brand, name]);

    const canSave = !!spaceId && !!typeId && !!name.trim() && !isSaving;

    const remove = async () => {
        if (!entry) return;
        try {
            await equipmentTestLibraryDao.deleteEntry(entry.id);
            log.info(`Deleted test library entry: ${entry.id}`);
            toast.success('Entry deleted');
            setConfirmDelete(false);
            onOpenChange(false);
        } catch (err) {
            log.error('Failed to delete test library entry', { err });
            toast.error('Delete failed', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        }
    };

    const save = async () => {
        if (!spaceId || !typeId || !name.trim()) return;
        setIsSaving(true);
        try {
            const now = new Date().toISOString() as Timestamp;
            const data: EquipmentTestLibraryEntry = {
                object: 'equipment-test-library-entry',
                schema: 1,
                id: entryId,
                spaceId,
                typeId,
                name: name.trim(),
                ...(brand.trim() ? { brand: brand.trim() } : {}),
                ...(modelNumber.trim() ? { modelNumber: modelNumber.trim() as ModelNumber } : {}),
                ...(serialNumber.trim() ? { serialNumber: serialNumber.trim() as SerialNumber } : {}),
                ...(productImage ? { productImage } : {}),
                ...(nameplateImage ? { nameplateImage } : {}),
                ...(notes.trim() ? { notes: notes.trim() } : {}),
                source: usedSearch ? 'web-sourced' : (entry?.source ?? 'own-photo'),
                createdAt: entry?.createdAt ?? now,
                updatedAt: now,
            };
            await equipmentTestLibraryDao.setEntry(entryId, data);
            log.info(`Saved test library entry: ${entryId}`, { typeId, spaceId });
            toast.success(entry ? 'Entry updated' : 'Entry added');
            onOpenChange(false);
        } catch (err) {
            log.error('Failed to save test library entry', { err });
            toast.error('Save failed', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{entry ? 'Edit entry' : 'Add entry'}</DialogTitle>
                    <DialogDescription>
                        A product photo and a nameplate photo, so the app's scan paths can be tested against equipment
                        nobody has on hand. Upload your own, or search the web for one.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 md:grid-cols-2">
                    <div className="min-w-0 space-y-2">
                        <Label>Space</Label>
                        <Select
                            value={spaceId ?? ''}
                            onValueChange={(value) => {
                                setSpaceId(value as SpaceId);
                                setTypeId(undefined);
                            }}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a space" />
                            </SelectTrigger>
                            <SelectContent>
                                {spaces.map((space) => (
                                    <SelectItem key={space.id} value={space.id}>
                                        {space.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="min-w-0 space-y-2">
                        <Label>Equipment type</Label>
                        <EquipmentTypeField
                            value={typeId}
                            onChange={setTypeId}
                            types={typesInSpace}
                            disabled={!spaceId}
                            placeholder={spaceId ? 'Select a type' : 'Pick a space first'}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="entry-name">Name</Label>
                        <Input id="entry-name" value={name} onChange={(event) => setName(event.target.value)} />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="entry-brand">Brand</Label>
                        <Input
                            id="entry-brand"
                            value={brand}
                            onChange={(event) => setBrand(event.target.value)}
                            placeholder="e.g. Carrier"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="entry-model">Model number</Label>
                        <Input
                            id="entry-model"
                            value={modelNumber}
                            onChange={(event) => setModelNumber(event.target.value)}
                            placeholder="As printed on the plate"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="entry-serial">Serial number</Label>
                        <Input
                            id="entry-serial"
                            value={serialNumber}
                            onChange={(event) => setSerialNumber(event.target.value)}
                            placeholder="Unlocks the serial-decode path"
                        />
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    <ImageSlotField
                        slot="product"
                        label="Product photo"
                        entryId={entryId}
                        typeTitle={selectedType?.title ?? ''}
                        brand={brand.trim() || undefined}
                        image={productImage}
                        onChange={(image, origin) => {
                            setProductImage(image);
                            if (origin === 'search') setUsedSearch(true);
                        }}
                    />
                    <ImageSlotField
                        slot="nameplate"
                        label="Nameplate photo"
                        entryId={entryId}
                        typeTitle={selectedType?.title ?? ''}
                        brand={brand.trim() || undefined}
                        image={nameplateImage}
                        onChange={(image, origin) => {
                            setNameplateImage(image);
                            if (origin === 'search') setUsedSearch(true);
                        }}
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="entry-notes">Notes</Label>
                    <Textarea
                        id="entry-notes"
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        placeholder="Anything worth knowing when testing with this — e.g. plate is hard to read, 5/S mix-up"
                        rows={2}
                    />
                </div>

                <DialogFooter className="sm:justify-between">
                    {/* Delete sits opposite Save, and only when there's something to delete — an
                        unsaved new entry has nothing behind it. */}
                    <div>
                        {entry && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setConfirmDelete(true)}
                                disabled={isSaving}
                            >
                                Delete
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={() => void save()} disabled={!canSave}>
                            {isSaving ? 'Saving…' : entry ? 'Save changes' : 'Add entry'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{entry?.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                            The entry and both photos are removed for good. Nothing else references it, so nothing
                            breaks — but it can't be undone, and a web-sourced photo would have to be found again.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void remove()}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
};
